import { SettingsKeyType } from "./Components/SettingsListView";
import StarFilledIconPath from "@coworker/apprestructured/src/shared/wrappers/FixaSSRIcon/paths/StarFilledIconPath";
import StarHalfFilledIconPath from "@coworker/apprestructured/src/shared/wrappers/FixaSSRIcon/paths/StarHalfFilledIconPath";
import {
  ActiveRoundObject,
  ChartData,
  SettingsDefault,
  SettingsResponse,
  ToolsCount,
} from "./Types";
import {
  DIVISION_HASH,
  FILTER_PERIOD_HASH,
  ROUND_HASH,
  TERTIALS,
  Tertial,
  Timestamps,
  typesOfShoppingToolsList,
  ShoppingToolType,
  NO_TOOL,
  MAX_CASHLINE_TOOLS,
} from "./constants";
import { temporaryCurrency } from "./temporaryCurrency";
import dayjs from "dayjs";
import { FilterValue } from "@coworker/apprestructured/src/shared/filters/types/filter";

export const getStartOfDay = (): number => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return today.getTime();
};

export function findSettingsObject(
  settingsList: SettingsDefault[] | SettingsResponse[],
  division: string
) {
  return settingsList?.find(
    (obj: SettingsDefault | SettingsResponse) => obj.division === division
  );
}

export function getCurrency(countryId: string) {
  return temporaryCurrency.find((country) => country.countryId === countryId)
    ?.currency;
}
const groupCurrency1 = {
  countryList: ["EUR", "CHF", "GBP", "USD"],
  currencyBlockAmount: 2000,
};
const groupCurrency2 = {
  countryList: ["INR", "JPY", "RSD"],
  currencyBlockAmount: 300000,
};
const groupCurrency3 = {
  countryList: ["PLN", "RON"],
  currencyBlockAmount: 10000,
};
const groupCurrency4 = {
  countryList: ["AUD", "CAD"],
  currencyBlockAmount: 3000,
};
const groupCurrency5 = {
  countryList: ["CZK"],
  currencyBlockAmount: 50000,
};
const groupCurrency6 = {
  countryList: ["HUF"],
  currencyBlockAmount: 700000,
};
const groupCurrency7 = {
  countryList: ["KRW"],
  currencyBlockAmount: 2500000,
};

export function getGroupCurrencyBlockAmount(currency: string): number {
  const currencyGroups = [
    groupCurrency1,
    groupCurrency2,
    groupCurrency3,
    groupCurrency4,
    groupCurrency5,
    groupCurrency6,
    groupCurrency7,
  ];

  for (const group of currencyGroups) {
    if (group.countryList.includes(currency)) {
      return group.currencyBlockAmount;
    }
  }

  return 20000;
}

export function scrollToTop(): void {
  const scrollableContainer = document.querySelector("div[id=main-content]");
  if (scrollableContainer) {
    scrollableContainer.scrollTo({
      top: 0,
      left: 0,
      behavior: "auto",
    });
  }
}

export function addShowroomToolsToMarkethall(
  settingsList: SettingsDefault[] | SettingsResponse[],
  activeSettings: SettingsDefault | SettingsResponse
) {
  const activeShowroomSettings: SettingsDefault | SettingsResponse =
    findSettingsObject(settingsList, DIVISION_HASH.SHOWROOM?.division ?? "")!;

  const foundMarkethallTools = Object.keys(activeSettings ?? []).filter(
    (key) => (activeSettings as SettingsKeyType)[key] === true
  );

  const foundShowroomTools = Object.keys(activeShowroomSettings ?? []).filter(
    (key) => (activeShowroomSettings as SettingsKeyType)[key] === true
  );

  const updatedMarkethallSettings = [...foundShowroomTools];

  for (const item of foundMarkethallTools) {
    if (!updatedMarkethallSettings.includes(item)) {
      updatedMarkethallSettings.push(item);
    }
  }

  return updatedMarkethallSettings;
}

export function getChartData(
  allTools: ChartData[],
  area: string | undefined,
  t: (key: string) => string
) {
  const tools = [
    {
      label: "noToolString",
      percentage: getToolData(allTools, area, "no_tool"),
      backgroundColor: "#DFDFDF",
    },
    {
      label: "yellowBagString",
      percentage: getToolData(allTools, area, "yellow_bag"),
      backgroundColor: "#F9DC4A",
    },
    {
      label: "blueBagToolString",
      percentage: getToolData(allTools, area, "blue_bag"),
      backgroundColor: "#4294CD",
    },
    {
      label: "yellowBasketString",
      percentage: getToolData(allTools, area, "yellow_basket"),
      backgroundColor: "#BC5825",
    },
    {
      label: "strollerString",
      percentage: getToolData(allTools, area, "stroller"),
      backgroundColor: "#24579E",
    },
    {
      label: "bagTrolleyString",
      percentage: getToolData(allTools, area, "bag_trolley"),
      backgroundColor: "#DF8CB1",
    },
    {
      label: "trolleyString",
      percentage: getToolData(allTools, area, "trolley"),
      backgroundColor: "#87B33E",
    },
    {
      label: "furnitureTrolleyString",
      percentage: getToolData(allTools, area, "furniture_trolley"),
      backgroundColor: "#2C673C",
    },
    {
      label: "multipleToolsString",
      percentage: getToolData(allTools, area, "multiple_tools"),
      backgroundColor: "#D04040",
    },
    {
      label: "ownBagString",
      percentage: getToolData(allTools, area, "own_bag"),
      backgroundColor: "#9F50BA",
    },
  ];

  const toolsWithValues = tools
    .filter((tool) => tool.percentage > 0)
    .sort((a, b) => b.percentage - a.percentage);

  const isSingleFullTool = toolsWithValues.length === 1;

  return {
    labels: toolsWithValues.map(
      (tool) => `${t(tool.label)}: ${tool.percentage}%`
    ),
    datasets: [
      {
        data: toolsWithValues.map((tool) => tool.percentage),
        backgroundColor: toolsWithValues.map((tool) => tool.backgroundColor),
        borderColor: "white",
        borderWidth: isSingleFullTool ? 0 : 2,
      },
    ],
  };
}

export function getToolData(
  allTools: ChartData[],
  area: string | undefined,
  tool: string
) {
  const dataForArea = allTools?.find((toolData) => toolData?.division === area);

  if (!dataForArea) return 0;

  const shoppingTools =
    area === DIVISION_HASH.SHOWROOM?.division
      ? dataForArea.initialShoppingTools
      : dataForArea.lastShoppingTools;

  const toolCount = shoppingTools?.[tool] ?? 0;

  return (
    Math.round(
      (toolCount /
        Object.values(shoppingTools).reduce((acc, curr) => acc + curr, 0)) *
        1000
    ) / 10
  );
}

export function createTimestampsFromDates(
  startDate: string | undefined,
  endDate: string | undefined
): Timestamps {
  const startDateTimestamp = Date.parse(startDate ?? "");

  const endDateParse = Date.parse(endDate ?? "");
  const endDateTimestamp = endDateParse + 24 * 60 * 60 * 1000 - 1;

  const timestamps = {
    startDate: startDateTimestamp,
    endDate: endDateTimestamp,
  };

  return timestamps;
}

export function getTertialYear(
  tertialNumber: number | undefined,
  period: string
): number {
  let currentYear = dayjs().year();
  if (tertialNumber === 1 && period === FILTER_PERIOD_HASH.LAST_TERTIAL) {
    currentYear--;
  }
  return currentYear;
}

function getTertialTimestamps(
  currentTertial: Tertial | undefined,
  period: string
): Timestamps {
  const foundTertial = TERTIALS.find(
    (tertial) => tertial.tertialNumber === currentTertial?.tertialNumber
  );

  const currentYear = getTertialYear(foundTertial?.tertialNumber, period);

  const timestamps = createTimestampsFromDates(
    `${currentYear}-${foundTertial?.startDate}`,
    `${currentYear}-${foundTertial?.endDate}`
  );

  return timestamps;
}

export function getTertialByPeriod(
  tertial: Tertial | undefined,
  period: string
): Timestamps {
  const currentYear = getTertialYear(tertial?.tertialNumber, period);

  const periodTertial = {
    tertialNumber: tertial?.tertialNumber,
    startDate: `${currentYear}-${tertial?.startDate}`,
    endDate: `${currentYear}-${tertial?.endDate}`,
  };

  const tertialWithTimestamps = getTertialTimestamps(periodTertial, period);

  return tertialWithTimestamps;
}

export function getTertial(period: string) {
  const today = dayjs().format("MM-DD");

  let tertial = TERTIALS.find(
    (tertial: Tertial) => tertial.startDate <= today && tertial.endDate >= today
  );
  if (FILTER_PERIOD_HASH.LAST_TERTIAL === period) {
    switch (tertial?.tertialNumber) {
      case 1:
        tertial = TERTIALS[2];
        break;
      case 2:
        tertial = TERTIALS[0];
        break;
      case 3:
        tertial = TERTIALS[1];
        break;
      default:
        return tertial;
    }
  }
  return tertial;
}

function findTertial(period: string): Timestamps | undefined {
  const tertial = getTertial(period);
  const periodTertial = getTertialByPeriod(tertial, period);

  return periodTertial;
}

export function getStartAndEndDateForChartData(
  period: string,
  customStartDate?: string | undefined,
  customEndDate?: string | undefined
): Timestamps {
  let startDate;
  let endDate;
  if (period === FILTER_PERIOD_HASH.TODAY) {
    const today = dayjs().format("YYYY-MM-DD");
    const timestamps = createTimestampsFromDates(today, today);

    startDate = timestamps.startDate;
    endDate = timestamps.endDate;
  } else if (
    period === FILTER_PERIOD_HASH.THIS_TERTIAL ||
    period === FILTER_PERIOD_HASH.LAST_TERTIAL
  ) {
    const tertial = findTertial(period);

    startDate = tertial?.startDate;
    endDate = tertial?.endDate;
  } else {
    const timestamps = createTimestampsFromDates(
      customStartDate,
      customEndDate
    );

    startDate = timestamps.startDate;
    endDate = timestamps.endDate;
  }
  return {
    startDate,
    endDate,
  };
}

export function getPeriodTitle(
  activePeriod: string,
  customStartDate: string,
  customEndDate: string,
  t: (key: string) => string
): string {
  if (activePeriod !== "customTitle" && activePeriod !== "custom")
    return t(`${activePeriod}String`);

  return `${dayjs(customStartDate).format("YYYY-MM-DD")} - ${dayjs(
    customEndDate
  ).format("YYYY-MM-DD")}`;
}
export interface StarConfig {
  color: string;
  icon: () => React.SVGProps<SVGElement>[];
}
export function createStarConfig(
  currentValueInProgress: number,
  starMilestoneValue: number
): StarConfig {
  switch (true) {
    case starMilestoneValue === 0:
      return currentValueInProgress < 50
        ? {
            icon: StarHalfFilledIconPath,
            color:
              currentValueInProgress < 25
                ? "var(--grey200)"
                : "var(--blue-medium)",
          }
        : { icon: StarFilledIconPath, color: "var(--blue-medium)" };
    case starMilestoneValue === 1:
      return currentValueInProgress < 100
        ? {
            icon: StarHalfFilledIconPath,
            color:
              currentValueInProgress < 75
                ? "var(--grey200)"
                : "var(--forest-green)",
          }
        : { icon: StarFilledIconPath, color: "var(--forest-green)" };
    case starMilestoneValue === 2:
      return currentValueInProgress < 150
        ? {
            icon: StarHalfFilledIconPath,
            color:
              currentValueInProgress < 125 ? "var(--grey200)" : "var(--violet)",
          }
        : { icon: StarFilledIconPath, color: "var(--violet)" };
    case starMilestoneValue === 3:
      return currentValueInProgress < 200
        ? {
            icon: StarHalfFilledIconPath,
            color:
              currentValueInProgress < 175
                ? "var(--grey200)"
                : "var(--grey718)",
          }
        : { icon: StarFilledIconPath, color: "var(--grey718)" };
    default:
      return { icon: StarFilledIconPath, color: "var(--grey200)" };
  }
}

export function getRoundData(value: number) {
  let color: string;
  let level: number;
  let maxValue: number;
  let displayValue: number;
  let displayMaxValue: number;
  let isLevelCompleted: boolean;
  let isHalfWayThere: boolean;

  const currentValue = typeof value === "string" ? parseFloat(value) : value;

  // Level 1
  if (currentValue >= 0 && currentValue <= 50) {
    color = "var(--blue-medium)";
    maxValue = 50;
    level = 1;
    displayValue = currentValue;
    displayMaxValue = maxValue;
    isLevelCompleted = currentValue === 50;
    isHalfWayThere = currentValue >= 25;
    // Level 2
  } else if (currentValue > 50 && currentValue <= 100) {
    color = "var(--forest-green)";
    maxValue = 100;
    level = 2;
    displayValue = currentValue - 50;
    displayMaxValue = maxValue - 50;

    isLevelCompleted = currentValue === 100;
    isHalfWayThere = currentValue >= 75;
    // Level 3
  } else if (currentValue > 100 && currentValue <= 150) {
    color = "var(--violet)";
    maxValue = 150;
    level = 3;
    displayValue = currentValue - 100;
    displayMaxValue = maxValue - 100;
    isLevelCompleted = currentValue === 150;
    isHalfWayThere = currentValue >= 125;
    // Level 4
  } else {
    color = "var(--grey718)";
    maxValue = 200;
    level = 4;
    displayValue = currentValue - 150;
    displayMaxValue = maxValue - 150;
    isLevelCompleted = currentValue >= 200;
    isHalfWayThere = currentValue >= 175;
  }

  return {
    color,
    currentValue,
    maxValue,
    level,
    displayValue,
    displayMaxValue,
    isLevelCompleted,
    isHalfWayThere,
  };
}

export function cleanKeyNumberPadValue(value: string) {
  value = value || "";
  value = value.replace(/[^0-9,.]/g, "");
  value = value.replace(/,/g, ".");

  return value;
}

export function getFormattedArea(activeArea: string) {
  switch (activeArea) {
    case "showroomOW":
      return "showroom";
    case "markethallOW":
      return "markethall";
    case "otherAreaCashline":
      return "cashline";
    default:
      return "";
  }
}

export function checkLastShoppingToolsValues(lastShoppingTools: {
  [key: string]: number;
}) {
  return Object.values(lastShoppingTools).every((value) => value === 0);
}

export function sortAndDisableSettingsList(toolsToBeDisabledList: string[]) {
  const removedSettingsTools = typesOfShoppingToolsList().filter(
    (setting) => !toolsToBeDisabledList.includes(setting.name)
  );

  const mySettingsList = typesOfShoppingToolsList().filter(
    (setting) =>
      !removedSettingsTools.some(
        (removedSettings) => setting.name === removedSettings.name
      )
  );

  const sortedSettingsList = [...mySettingsList, ...removedSettingsTools];
  return sortedSettingsList;
}

export function checkActiveRound(round: string): ActiveRoundObject {
  const roundTimings = {
    [ROUND_HASH.MORNING.round]: { start: 7, end: 13 },
    [ROUND_HASH.MIDDAY.round]: { start: 11, end: 16 },
    [ROUND_HASH.AFTERNOON.round]: { start: 15, end: 21 },
  } as { [key: string]: { start: number; end: number } };
  const timings = roundTimings[round];
  if (!timings) {
    return { isActiveRound: false, startTime: 0, endTime: 0 };
  }
  const currentHour = dayjs().hour();
  const active = currentHour >= timings.start && currentHour < timings.end;
  return {
    isActiveRound: active,
    startTime: timings.start,
    endTime: timings.end,
  };
}

export function hasNoTools(
  lastShoppingTools: ToolsCount,
  type: ShoppingToolType
): boolean {
  return (lastShoppingTools["no_tool"] ?? 0) > 0 && type.name !== NO_TOOL;
}
export function checkActiveRoundFilter(
  title: string,
  shoppingToolsFilters: FilterValue[]
) {
  if (title.includes("String")) {
    title = title.replace("String", "");
  }

  const activeRound =
    (shoppingToolsFilters.find((filter) => filter.filterId === "round")
      ?.value as string) ?? "allRounds";

  return title === activeRound || activeRound === "allRounds";
}
export function getFormatedRound(round: string) {
  if (round === "allRounds") return "";
  if (round.includes("Round")) {
    round = round.replace("Round", "");
  }

  return round;
}
export function sanitizeProgressValue(
  todaysProgress: number | void | undefined
) {
  if (todaysProgress == null) {
    return 0;
  }
  return todaysProgress;
}

export function isMaxToolsReachedCashline(
  cashlineTools: ToolsCount,
  toolLimit: number
): boolean {
  let totalCashlineTools = 0;
  Object.values(cashlineTools).forEach((count) => {
    totalCashlineTools += count;
  });
  return totalCashlineTools < toolLimit ? true : false;
}

export function getMaxToolValue(isWarningShown: boolean): number {
  if (isWarningShown) {
    return MAX_CASHLINE_TOOLS;
  }
  return 0;
}
